<template>
    <section class="search-detail-page">
        <v-overlay :value="inFlight">
            <v-progress-circular
                indeterminate
                size="64"
            ></v-progress-circular>
        </v-overlay>
        <v-row>
            <v-col class="package-information">
                <div class="package-information__cells">
                    <strong>Recipient:</strong>
                    <strong>Type:</strong>
                    <strong>Tracking #:</strong>
                    <strong>Reference #:</strong>
                    <strong>Sender:</strong>
                    <strong>Carrier:</strong>
                </div>
                <div class="package-information__cells">
                    <span>{{ customerFullName ? customerFullName : "Not Found" }} - {{ customerExternalRefNum }}</span>
                    <span>{{ mailTypeFriendly ? mailTypeFriendly : "Not Found" }}</span>
                    <span>{{ trackingNumber ? trackingNumber : "Not Found" }}</span>
                    <span>{{ refNum ? refNum : "Not Found" }}</span>
                    <span>{{ sender ? sender : "Not Found" }}</span>
                    <span>{{ mailCarrierFriendly ? mailCarrierFriendly : "Not Found" }}</span>
                </div>
            </v-col>
            <v-col class="text-right">
                <ClButton
                    :rounded="false"
                    size="large"
                    buttonText="Back"
                    @click="$router.push( { path: MailSearchRoute.path } )"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-list v-if="transactions">
                <v-list-group
                    v-for="transaction in transactions"
                    :key="transaction.createdAt"
                >
                    <template v-slot:activator>
                        <v-list-item-content>
                            <div class="transaction-item-heading">
                                <span>{{ transaction.createdAt }}</span>
                                <span>{{ transaction.transTypeFriendly }}</span>
                                <span>{{ transaction.destinationName }}</span>
                                <span>{{ transaction.userAccountName }}</span>
                                <div>
                                    <v-icon
                                        v-if="!!transaction.notes"
                                        left
                                    >
                                        mdi-note-text
                                    </v-icon>
                                    <v-icon
                                        v-if="transaction.photoUrls.length > 0"
                                        left
                                    >
                                        mdi-camera
                                    </v-icon>
                                </div>
                            </div>
                        </v-list-item-content>
                    </template>

                    <v-list-item>
                        <v-list-item-content>
                            <v-row
                                v-if="doesTransactionHaveDetails( transaction.photoUrls, transaction.notes )"
                                class="transaction-item-details"
                            >
                                <v-col cols="12" sm="2">
                                    <!-- Image and Image Modal -->
                                    <ClModal
                                        v-model="imageModal"
                                        maxWidth="800px"
                                        @keydown.esc="imageModal = false"
                                    >
                                        <!-- Use the Image to activate the Image Modal -->
                                        <template v-slot:activator="{ on, attrs }">
                                            <!-- Though we get an array for photoUrls, we do not allow for upload of more than one image per transaction.
                                            Therefore, we shall only have to display one -->
                                            <v-img
                                                :src="transaction.photoUrls[ 0 ]"
                                                class="image-modal-thumbnail"
                                                role="link"
                                                alt="link to image of mail item"
                                                v-bind="attrs"
                                                tabindex="0"
                                                v-on="on"
                                                @keypress.enter="imageModal = true"
                                                @keypress.space="imageModal = true"
                                            />
                                        </template>
                                        <v-card>
                                            <v-card-title class="modal-image-title">
                                                Detail
                                                <v-spacer />
                                                <ClButton
                                                    icon
                                                    color="white"
                                                    title="Close Image Modal"
                                                    aria-label="close"
                                                    @click="imageModal = false"
                                                >
                                                    <v-icon>mdi-close</v-icon>
                                                </ClButton>
                                            </v-card-title>
                                            <v-card-text class="modal-image-text">
                                                <v-img :src="transaction.photoUrls[ 0 ]"/>
                                            </v-card-text>
                                            <v-card-actions>
                                                <v-spacer/>
                                                <ClButton
                                                    buttonText="OK"
                                                    :rounded="false"
                                                    size="large"
                                                    class="modal-image-action-button"
                                                    @click="imageModal = false"
                                                />
                                            </v-card-actions>
                                        </v-card>
                                    </ClModal>
                                </v-col>
                                <v-col>
                                    <p v-if="transaction.transTypeFriendly === 'Void'">
                                        Void Reason:
                                    </p>
                                    <p>{{ transaction.notes }}</p>
                                </v-col>
                            </v-row>
                            <p v-else>No additional information.</p>
                        </v-list-item-content>
                    </v-list-item>
                </v-list-group>
            </v-list>
        </v-row>
    </section>
</template>

<script>

// Vuex
import { mapState } from 'vuex';

// Routes
import MailSearchRoute from '@/router/routes/Mail/routes/Search';

export default {
    name: 'SearchDetail',
    data() {
        return {
            MailSearchRoute,
            imageModal: false,
        };
    },
    computed: {
        ...mapState( {
            customerFullName: ( state ) => state.Mail.Search.currentMailItem.customerFullName,
            customerExternalRefNum: ( state ) => state.Mail.Search.currentMailItem.customerExternalRefNum,
            mailTypeFriendly: ( state ) => state.Mail.Search.currentMailItem.mailTypeFriendly,
            mailCarrierFriendly: ( state ) => state.Mail.Search.currentMailItem.mailCarrierFriendly,
            trackingNumber: ( state ) => state.Mail.Search.currentMailItem.trackingNumber,
            refNum: ( state ) => state.Mail.Search.currentMailItem.refNum,
            sender: ( state ) => state.Mail.Search.currentMailItem.sender,
            transactions: ( state ) => state.Mail.Search.currentMailItem.mailTransHistory,
            id: ( state ) => state.Mail.Search.currentMailItem.id,
            inFlight: ( state ) => state.Mail.Search.results.inFlight,
        } ),
    },
    mounted() {
        this.$store.dispatch( 'Mail/Search/setMailItemDetails', this.$router.currentRoute?.params?.id );
    },
    beforeDestroy() {
        this.$store.dispatch( 'Mail/Search/clearCurrentMailItem' );
    },
    methods: {
        doesTransactionHaveDetails( transImageArray, transNote ) {
            return transImageArray.length > 0 || transNote;
        },
    },
};
</script>

<style lang="scss" scoped>
.search-detail-page {
    margin-top: 1rem;

    .package-information {
        display: grid;
        grid-template-columns: 1fr 7fr;
        grid-gap: 1rem;

        &__cells>* {
            display: block;
            white-space: nowrap;
        }
    }

    ::v-deep .v-list {
        width: 100%;

        .v-list-group {
            border: 1px solid $colors-black;
            border-bottom: 0px;

            &:last-of-type {
                border-bottom: 1px solid $colors-black;
            }
        }
    }

    .transaction-item-heading {
        display: grid;
        grid-template-columns: repeat( 5, 1fr );
    }

    .image-modal-thumbnail {
        cursor: pointer;
    }
}
// Modal is instantiated outside the scope of the root element
.modal-image-title {
    color: $colors-white;
    background-color: $colors-primary;

    &__close {
    color: $colors-white;
    }
}

.modal-image-text {
    margin-top: 1rem;
}

.modal-image-action-button {
    margin-bottom: 1rem;
}
</style>
